import React, { useState, useEffect } from 'react';
import './MarketplacePracticeProfile.scss';
import MarketplacePracticeLocationsTable from './MarketplacePracticeLocationsTable/MarketplacePracticeLocationsTable';
import ImageCarousel from '../../ImageCarousel/ImageCarousel';
import {
    getCarouselImages,
    resolveEffectiveImagesLocationId,
} from '../DisclosedProfile/locationImages';
import { Input } from '@availity/form';
import { Col, FormGroup, Row } from 'reactstrap';
import InfoTooltip from '../../info-tooltip/info-tooltip';
import {
    OwnershipType,
    OwnershipTypeOptions,
} from '../../../slices/marketplace/timeline/models/enums/ownership-type.enum';
import {
    RevenueGrowthRate,
    RevenueGrowthRates,
} from '../../../slices/marketplace/timeline/models/enums/revenue-growth.enum';
import { PracticeProfileInfo } from '../../../slices/profiles-data-slice/models/practice-profile-info';
import { useFormikContext, getIn } from 'formik';
import {
    DataSynchronizationValidator,
    SyncField,
} from '../../DataSynchronizationValidator/DataSynchronizationValidator';
import { useServiceFocuses } from '../../../custom-hooks/useServiceFocuses';
import {
    isOtherFocus,
} from '../../../containers/UserPages/Login/Modals/PracticeRegistrationForm/utils';
import { StarRating } from '../../StarRating/StarRating';
import { NotificationFlag } from '../../NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../store/selectors';
import { LoadMarketplaceTemplatePricing } from '../../../slices/marketplace/pricing/load-marketplace-pricing.thunk';
import { KnowledgeLibraryLink } from '../../KnowledgeLibraryLink/KnowledgeLibraryLink';

interface MarketplacePracticeProfileProps {
    practice: PracticeProfileInfo;
    profileId: string;
    fieldPrefix: string;
    setFormFieldValue(name: string, value: any): void;
    onAddLocationClicked(): void;
    removeLocation(locationIndex: number): void;
    isEditable?: boolean;
    isAdmin?: boolean;
    isBroker?: boolean;
    dataSyncTrigger?: number;
    timelineId?: number;
}

export const MarketplacePracticeProfile = (
    props: MarketplacePracticeProfileProps
) => {
    const { isEditable = true, isAdmin = false, isBroker = false, timelineId } = props;
    const canEditRatings = isAdmin || isBroker;
    const practice = props.practice;
    const serviceFocuses = useServiceFocuses();
    const { values, errors } = useFormikContext();
    const dispatch = useDispatch() as any;
    const considerationFlexibilityKlContentId = useStateSelector(
        (state) => state.marketplacePricing.pricing.considerationFlexibilityKlContentId
    );
    const ownerPostClosingInvolvementKlContentId = useStateSelector(
        (state) => state.marketplacePricing.pricing.ownerPostClosingInvolvementKlContentId
    );
    const considerationFlexibilityKlLinkText = useStateSelector(
        (state) => state.marketplacePricing.pricing.considerationFlexibilityKlLinkText
    );
    const ownerPostClosingInvolvementKlLinkText = useStateSelector(
        (state) => state.marketplacePricing.pricing.ownerPostClosingInvolvementKlLinkText
    );
    const considerationFlexibilityKlContentTitle = useStateSelector(
        (state) => state.marketplacePricing.pricing.considerationFlexibilityKlContentTitle
    );
    const ownerPostClosingInvolvementKlContentTitle = useStateSelector(
        (state) => state.marketplacePricing.pricing.ownerPostClosingInvolvementKlContentTitle
    );

    useEffect(() => {
        dispatch(LoadMarketplaceTemplatePricing());
    }, []);

    // "Show Images From" selection for the practice-images carousel — mirrors the
    // preview behavior: defaults to the first location.
    const practiceLocations = practice.practiceLocations || [];
    const [selectedImagesLocationId, setSelectedImagesLocationId] =
        useState<string>('');
    const effectiveImagesLocationId = resolveEffectiveImagesLocationId(
        practiceLocations,
        selectedImagesLocationId
    );
    const carouselImages = getCarouselImages(
        practiceLocations,
        practice.uploads,
        effectiveImagesLocationId
    );

    const formatRatingDisplay = (v: unknown): string => {
        if (v === '' || v == null) return '';
        const n = Number(v);
        if (isNaN(n)) return '';
        const clamped = Math.min(5, Math.max(0, n));
        return clamped % 1 === 0 ? clamped.toString() : clamped.toFixed(1);
    };

    const isOtherPracticeFocusInputVisible = () => {
        return isOtherFocus(
            serviceFocuses,
            getIn(values, `${props.fieldPrefix}.practiceFocusType`)
        );
    };

    const renderDataSynchronizationValidator = () => {
        if (isBroker) {
            return null;
        }

        const currentPractice = getIn(values, props.fieldPrefix);
        const practiceId = currentPractice?.practiceId || practice.practiceId;
        const fteDvms = currentPractice?.fteDvms || practice.fteDvms || 0;

        if (!practiceId) {
            return null;
        }

        return (
            <DataSynchronizationValidator
                key={`practice-sync-validator-${practice.id}`}
                practiceId={practiceId}
                fteDvm={fteDvms}
                ltmRevenue={0}
                sync={props.dataSyncTrigger}
                fieldsToSync={[SyncField.FteDvm]}
            />
        );
    };

    const practiceFieldBadge = (field: string, includePrefix?: boolean) =>
        timelineId ? (
            <NotificationFlag
                flagClassName="notification-flag-wrapper"
                doNotRemove={true}
                includePrefix={includePrefix}
                eventCode={`${NotificationFlagsCategories.MpCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${practice.id}.${field}`}
            />
        ) : null;

    return (
        <div className="marketplace-practice-profile">
            <section className="practice-information">
                <div className="practice-form-container">
                    <Row>
                        <Col md={6} className="practice-form">
                            <div className="section-title mb-4">
                                <h2>Practice Information</h2>
                            </div>
                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        <span className="label-with-badge">
                                            Practice Name
                                            {practiceFieldBadge('PracticeName')}
                                        </span>
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.practiceName`}
                                            type="text"
                                            className="form-control"
                                            disabled={!isEditable}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        <span className="label-with-badge">
                                            Owner Name
                                            {practiceFieldBadge('OwnerFullName')}
                                        </span>
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.ownerFullName`}
                                            type="text"
                                            className="form-control"
                                            disabled={!isEditable}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        <span className="label-with-badge">
                                            Ownership Type
                                            {practiceFieldBadge('PracticeOwnershipType')}
                                        </span>
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.practiceOwnershipType`}
                                            type="select"
                                            className="form-control"
                                            disabled={!isEditable}
                                            defaultValue={
                                                OwnershipType.SingleOwner
                                            }>
                                            {Object.entries(
                                                OwnershipTypeOptions
                                            ).map(([key, value]) => (
                                                <option key={key} value={key}>
                                                    {value}
                                                </option>
                                            ))}
                                        </Input>
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        <span className="label-with-badge">
                                            Service Focus
                                            {practiceFieldBadge('PracticeFocusType')}
                                        </span>
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.practiceFocusType`}
                                            type="select"
                                            className="form-control"
                                            disabled={!isEditable}
                                            defaultValue={1}>
                                            {serviceFocuses.map((option) => (
                                                <option
                                                    key={option.id}
                                                    value={option.id}>
                                                    {option.name}
                                                </option>
                                            ))}
                                        </Input>
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup
                                hidden={!isOtherPracticeFocusInputVisible()}>
                                <Row>
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        Specify Service Focus
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.otherPracticeType`}
                                            type="text"
                                            className="form-control"
                                            disabled={!isEditable}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        <span className="label-with-badge">
                                            Number of FTE DVMs
                                            {practiceFieldBadge('FteDvms')}
                                        </span>
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.fteDvms`}
                                            type="number"
                                            className="form-control"
                                            readOnly={true}
                                            disabled={!isEditable}
                                            defaultValue={0}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        <span className="label-with-badge">
                                            Revenue Growth Rate
                                            {practiceFieldBadge('RevenueGrowthRate')}
                                        </span>
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.revenueGrowthRate`}
                                            type="select"
                                            className="form-control"
                                            disabled={!isEditable}
                                            defaultValue={
                                                RevenueGrowthRate.noGrowth
                                            }>
                                            {RevenueGrowthRates.map(
                                                (option) => (
                                                    <option
                                                        key={option.id}
                                                        value={option.id}>
                                                        {option.name}
                                                    </option>
                                                )
                                            )}
                                        </Input>
                                    </Col>
                                </Row>
                            </FormGroup>

                            {!canEditRatings && getIn(values, `${props.fieldPrefix}.averageRating`) != null && (
                                <FormGroup className="star-rating-form-group">
                                    <Row className="horizontal-form-group">
                                        <Col md={3} className="col-form-label" tag="label">
                                            <span className="label-with-badge">
                                                Average Rating
                                                {practiceFieldBadge('AverageRating')}
                                            </span>
                                        </Col>
                                        <Col md={7}>
                                            <StarRating
                                                value={getIn(values, `${props.fieldPrefix}.averageRating`)}
                                                size="md"
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>
                            )}
                            {canEditRatings && (
                            <FormGroup className="star-rating-form-group">
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        <span className="label-with-badge">
                                            Average Rating
                                            {practiceFieldBadge('AverageRating')}
                                        </span>
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.averageRating`}
                                            type="number"
                                            step="0.1"
                                            min={0}
                                            max={5}
                                            className="form-control"
                                            value={formatRatingDisplay(
                                                getIn(values, `${props.fieldPrefix}.averageRating`)
                                            )}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                props.setFormFieldValue(
                                                    `${props.fieldPrefix}.averageRating`,
                                                    e.target.value
                                                )
                                            }
                                            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                                props.setFormFieldValue(
                                                    `${props.fieldPrefix}.averageRating`,
                                                    e.target.value
                                                )
                                            }
                                        />
                                        {getIn(errors, `${props.fieldPrefix}.averageRating`) && (
                                            <div className="invalid-feedback d-block">
                                                {getIn(errors, `${props.fieldPrefix}.averageRating`)}
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                            </FormGroup>
                            )}
                        </Col>
                        <Col
                            md={6}
                            className="practice-form practice-images-col">
                            <div className="section-title section-title--practice-images mb-4">
                                <h2>Practice Images</h2>
                            </div>
                            <ImageCarousel
                                key={effectiveImagesLocationId || 'no-location'}
                                images={carouselImages}
                                autoPlayInterval={5000}
                                showArrows={true}
                                showDots={true}
                                maxDots={5}
                                className="practice-carousel practice-carousel--edit"
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <FormGroup className="description-container">
                                <label>
                                    <span className="label-with-badge">
                                        Practice Description
                                        {practiceFieldBadge('Description')}
                                    </span>
                                </label>
                                <Input
                                    name={`${props.fieldPrefix}.description`}
                                    type="textarea"
                                    rows={8}
                                    className="form-control"
                                    disabled={!isEditable}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <FormGroup className="description-container">
                                <label>
                                    <span className="label-with-badge">
                                        Owner Post-Closing Involvement and Consideration Flexibility
                                        <InfoTooltip
                                            idText="consideration-flexibility-tooltip"
                                            text="Please indicate what types of post-closing involvement and purchase price consideration you are willing to consider. Links to discussion of alternatives for purchase price consideration and post-closing involvement are provided here so you can familiarize yourself with the issues before you share your perspectives with potentially interested buyers."
                                            place="top"
                                        />
                                        {practiceFieldBadge('OwnerPostClosingInvolvementAndConsiderationFlexibility')}
                                        {!isBroker && (
                                            <span className="kl-links">
                                                <KnowledgeLibraryLink
                                                    contentId={considerationFlexibilityKlContentId}
                                                    text={considerationFlexibilityKlLinkText || considerationFlexibilityKlContentTitle}
                                                    isAdmin={isAdmin}
                                                />
                                                <KnowledgeLibraryLink
                                                    contentId={ownerPostClosingInvolvementKlContentId}
                                                    text={ownerPostClosingInvolvementKlLinkText || ownerPostClosingInvolvementKlContentTitle}
                                                    isAdmin={isAdmin}
                                                />
                                            </span>
                                        )}
                                    </span>
                                </label>
                                <Input
                                    name={`${props.fieldPrefix}.ownerPostClosingInvolvementAndConsiderationFlexibility`}
                                    type="textarea"
                                    rows={8}
                                    className="form-control"
                                    disabled={!isEditable}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </div>
            </section>

            {practice.practiceLocations?.length ? (
                <MarketplacePracticeLocationsTable
                        locations={practice.practiceLocations}
                        fieldPrefix={`${props.fieldPrefix}.practiceLocations`}
                        setFormFieldValue={props.setFormFieldValue}
                        removeLocation={props.removeLocation}
                        isEditable={isEditable}
                        canEditRatings={canEditRatings}
                        timelineId={timelineId}
                        practiceId={practice.id}
                        profileId={props.profileId}
                        practiceUploads={practice.uploads || []}
                        showImagesFromLocationId={effectiveImagesLocationId}
                        onShowImagesFromChange={setSelectedImagesLocationId}
                    />
            ) : (
                <></>
            )}
            {isEditable && (
                <div
                    className="link-button"
                    onClick={props.onAddLocationClicked}>
                    {practice.practiceLocations?.length
                        ? '+ Add Another Location'
                        : '+ Add Location'}
                </div>
            )}

            {renderDataSynchronizationValidator()}
        </div>
    );
};
